import qrcode from 'qrcode-generator'

import { env } from '@/shared/config'

const QUIET_ZONE_MODULES = 4
const DEFAULT_MODULE_SIZE = 8
const DOWNLOAD_MODULE_SIZE = 20
const LOGO_SRC = '/blindbook-logo.svg'
const LOGO_WIDTH_RATIO = 0.22
const LOGO_PADDING_RATIO = 0.3

export type QrRenderOptions = {
  moduleSize?: number
  darkColor?: string
  lightColor?: string
}

let logoRequest: Promise<HTMLImageElement | null> | null = null

function loadLogo(): Promise<HTMLImageElement | null> {
  if (!logoRequest) {
    logoRequest = new Promise((resolve) => {
      const image = new Image()
      image.onload = () => resolve(image)
      image.onerror = () => resolve(null)
      image.src = LOGO_SRC
    })
  }
  return logoRequest
}

function drawLogo(
  context: CanvasRenderingContext2D,
  logo: HTMLImageElement,
  size: number,
  lightColor: string,
): void {
  const logoWidth = size * LOGO_WIDTH_RATIO
  const logoHeight = (logoWidth * logo.height) / logo.width
  const padding = logoHeight * LOGO_PADDING_RATIO
  const patchWidth = logoWidth + padding * 2
  const patchHeight = logoHeight + padding * 2

  context.fillStyle = lightColor
  context.fillRect(
    (size - patchWidth) / 2,
    (size - patchHeight) / 2,
    patchWidth,
    patchHeight,
  )
  context.drawImage(
    logo,
    (size - logoWidth) / 2,
    (size - logoHeight) / 2,
    logoWidth,
    logoHeight,
  )
}

export async function qrDataUrl(
  value: string,
  options: QrRenderOptions = {},
): Promise<string> {
  const moduleSize = options.moduleSize ?? DEFAULT_MODULE_SIZE
  const darkColor = options.darkColor ?? '#000000'
  const lightColor = options.lightColor ?? '#ffffff'

  const qr = qrcode(0, 'H')
  qr.addData(value)
  qr.make()

  const modules = qr.getModuleCount()
  const size = (modules + QUIET_ZONE_MODULES * 2) * moduleSize

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) return ''

  context.fillStyle = lightColor
  context.fillRect(0, 0, size, size)
  context.fillStyle = darkColor

  for (let row = 0; row < modules; row += 1) {
    for (let column = 0; column < modules; column += 1) {
      if (!qr.isDark(row, column)) continue
      context.fillRect(
        (column + QUIET_ZONE_MODULES) * moduleSize,
        (row + QUIET_ZONE_MODULES) * moduleSize,
        moduleSize,
        moduleSize,
      )
    }
  }

  const logo = await loadLogo()
  if (logo) {
    drawLogo(context, logo, size, lightColor)
  }

  return canvas.toDataURL('image/png')
}

export function qrDownloadDataUrl(value: string): Promise<string> {
  return qrDataUrl(value, { moduleSize: DOWNLOAD_MODULE_SIZE })
}

export function qrCodePublicUrl(codeId: string): string {
  return `${env.publicBaseUrl}/public/qr/${codeId}`
}

export function downloadQrDataUrl(dataUrl: string, filename: string): void {
  const anchor = document.createElement('a')
  anchor.href = dataUrl
  anchor.download = filename
  anchor.style.display = 'none'
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
}
